import React from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

const Container = styled.div`
  min-height: calc(100vh - var(--header-height));
  padding: 2rem 1rem;
  max-width: 800px;
  margin: 0 auto;
  margin-top: var(--header-height);

  @media (min-width: 768px) {
    padding: 3rem 2rem;
  }
`;

const Title = styled.h1`
  font-size: 2.5rem;
  margin-bottom: 1rem;
  color: var(--text-color);
`;

const LastUpdated = styled.p`
  color: var(--gray-dark);
  margin-bottom: 2rem;
  font-style: italic;
`;

const Section = styled.section`
  margin-bottom: 2rem;

  h2 {
    font-size: 1.5rem;
    margin-bottom: 1rem;
    color: var(--text-color);
  }

  h3 {
    font-size: 1.2rem;
    margin-bottom: 0.75rem;
    color: var(--text-color);
  }

  p {
    line-height: 1.8;
    color: var(--gray-dark);
    margin-bottom: 1rem;
  }

  ul {
    margin-left: 2rem;
    margin-bottom: 1rem;
    line-height: 1.8;
    color: var(--gray-dark);
  }

  li {
    margin-bottom: 0.5rem;
  }
`;

const ContactBox = styled.div`
  background-color: var(--gray-light);
  padding: 1.5rem;
  border-radius: 8px;
  margin-top: 2rem;

  h3 {
    margin-bottom: 1rem;
    color: var(--text-color);
  }

  p {
    margin-bottom: 0.5rem;
    line-height: 1.6;
  }
`;

const PrivacyPolicy: React.FC = () => {
  const { t } = useTranslation();

  return (
    <Container>
      <Title>{t('privacyPolicy.title')}</Title>

      <Section>
        <p>{t('privacyPolicy.intro')}</p>
      </Section>

      <Section>
        <h2>{t('privacyPolicy.section1.heading')}</h2>
        <h3>{t('privacyPolicy.section1.personalInfoHeading')}</h3>
        <p>{t('privacyPolicy.section1.personalInfoIntro')}</p>
        <ul>
          <li>{t('privacyPolicy.section1.personalInfoItem1')}</li>
          <li>{t('privacyPolicy.section1.personalInfoItem2')}</li>
          <li>{t('privacyPolicy.section1.personalInfoItem3')}</li>
          <li>{t('privacyPolicy.section1.personalInfoItem4')}</li>
          <li>{t('privacyPolicy.section1.personalInfoItem5')}</li>
        </ul>

        <h3>{t('privacyPolicy.section1.autoInfoHeading')}</h3>
        <p>{t('privacyPolicy.section1.autoInfoIntro')}</p>
        <ul>
          <li>{t('privacyPolicy.section1.autoInfoItem1')}</li>
          <li>{t('privacyPolicy.section1.autoInfoItem2')}</li>
          <li>{t('privacyPolicy.section1.autoInfoItem3')}</li>
          <li>{t('privacyPolicy.section1.autoInfoItem4')}</li>
          <li>{t('privacyPolicy.section1.autoInfoItem5')}</li>
        </ul>
      </Section>

      <Section>
        <h2>{t('privacyPolicy.section2.heading')}</h2>
        <p>{t('privacyPolicy.section2.intro')}</p>
        <ul>
          <li><strong>{t('privacyPolicy.section2.contractLabel')}</strong> {t('privacyPolicy.section2.contractText')}</li>
          <li><strong>{t('privacyPolicy.section2.legitimateInterestsLabel')}</strong> {t('privacyPolicy.section2.legitimateInterestsText')}</li>
          <li><strong>{t('privacyPolicy.section2.consentLabel')}</strong> {t('privacyPolicy.section2.consentText')}</li>
          <li><strong>{t('privacyPolicy.section2.legalObligationLabel')}</strong> {t('privacyPolicy.section2.legalObligationText')}</li>
        </ul>
      </Section>

      <Section>
        <h2>{t('privacyPolicy.section3.heading')}</h2>
        <p>{t('privacyPolicy.section3.intro')}</p>
        <ul>
          <li>{t('privacyPolicy.section3.item1')}</li>
          <li>{t('privacyPolicy.section3.item2')}</li>
          <li>{t('privacyPolicy.section3.item3')}</li>
          <li>{t('privacyPolicy.section3.item4')}</li>
          <li>{t('privacyPolicy.section3.item5')}</li>
          <li>{t('privacyPolicy.section3.item6')}</li>
          <li>{t('privacyPolicy.section3.item7')}</li>
        </ul>
      </Section>

      <Section>
        <h2>{t('privacyPolicy.section4.heading')}</h2>
        <p>{t('privacyPolicy.section4.intro')}</p>
        <ul>
          <li><strong>{t('privacyPolicy.section4.serviceProvidersLabel')}</strong> {t('privacyPolicy.section4.serviceProvidersText')}</li>
          <li><strong>{t('privacyPolicy.section4.legalRequirementsLabel')}</strong> {t('privacyPolicy.section4.legalRequirementsText')}</li>
          <li><strong>{t('privacyPolicy.section4.businessTransfersLabel')}</strong> {t('privacyPolicy.section4.businessTransfersText')}</li>
          <li><strong>{t('privacyPolicy.section4.consentLabel')}</strong> {t('privacyPolicy.section4.consentText')}</li>
        </ul>
      </Section>

      <Section>
        <h2>{t('privacyPolicy.section5.heading')}</h2>
        <p>{t('privacyPolicy.section5.intro')}</p>
        <ul>
          <li>{t('privacyPolicy.section5.item1')}</li>
          <li>{t('privacyPolicy.section5.item2')}</li>
          <li>{t('privacyPolicy.section5.item3')}</li>
          <li>{t('privacyPolicy.section5.item4')}</li>
        </ul>
      </Section>

      <Section>
        <h2>{t('privacyPolicy.section6.heading')}</h2>
        <p>{t('privacyPolicy.section6.intro')}</p>
        <ul>
          <li>{t('privacyPolicy.section6.item1')}</li>
          <li>{t('privacyPolicy.section6.item2')}</li>
          <li>{t('privacyPolicy.section6.item3')}</li>
        </ul>
      </Section>

      <Section>
        <h2>{t('privacyPolicy.section7.heading')}</h2>
        <p>{t('privacyPolicy.section7.intro')}</p>
        <ul>
          <li><strong>{t('privacyPolicy.section7.accessLabel')}</strong> {t('privacyPolicy.section7.accessText')}</li>
          <li><strong>{t('privacyPolicy.section7.rectificationLabel')}</strong> {t('privacyPolicy.section7.rectificationText')}</li>
          <li><strong>{t('privacyPolicy.section7.erasureLabel')}</strong> {t('privacyPolicy.section7.erasureText')}</li>
          <li><strong>{t('privacyPolicy.section7.restrictionLabel')}</strong> {t('privacyPolicy.section7.restrictionText')}</li>
          <li><strong>{t('privacyPolicy.section7.portabilityLabel')}</strong> {t('privacyPolicy.section7.portabilityText')}</li>
          <li><strong>{t('privacyPolicy.section7.objectLabel')}</strong> {t('privacyPolicy.section7.objectText')}</li>
          <li><strong>{t('privacyPolicy.section7.withdrawConsentLabel')}</strong> {t('privacyPolicy.section7.withdrawConsentText')}</li>
        </ul>
      </Section>

      <Section>
        <h2>{t('privacyPolicy.section8.heading')}</h2>
        <p>{t('privacyPolicy.section8.text')}</p>
      </Section>

      <Section>
        <h2>{t('privacyPolicy.section9.heading')}</h2>
        <p>{t('privacyPolicy.section9.text')}</p>
      </Section>

      <Section>
        <h2>{t('privacyPolicy.section10.heading')}</h2>
        <p>{t('privacyPolicy.section10.text')}</p>
      </Section>

      <Section>
        <h2>{t('privacyPolicy.section11.heading')}</h2>
        <p>{t('privacyPolicy.section11.text')}</p>
      </Section>

      <Section>
        <h2>{t('privacyPolicy.section12.heading')}</h2>
        <p>{t('privacyPolicy.section12.text')}</p>
      </Section>

      <ContactBox>
        <h3>{t('privacyPolicy.contactBox.heading')}</h3>
        <p><strong>{t('privacyPolicy.contactBox.companyName')}</strong></p>
        <p>{t('privacyPolicy.contactBox.address')}</p>
        <p>{t('privacyPolicy.contactBox.email')}</p>
        <p>{t('privacyPolicy.contactBox.phone')}</p>
        <p>
          {t('privacyPolicy.contactBox.icoPre')}<a href="https://ico.org.uk" target="_blank" rel="noopener noreferrer">{t('privacyPolicy.contactBox.icoLinkText')}</a>
        </p>
      </ContactBox>
    </Container>
  );
};

export default PrivacyPolicy;
